import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';

const SVG_CENTER = 128;
const OUTER_RADIUS = 88;
const INNER_RADIUS = 56;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function clockAngleRad(step: number, stepsPerRevolution: number): number {
  return toRad(step * (360 / stepsPerRevolution) - 90);
}

export interface ClockNumber {
  label: string;
  value: number;
  x: number;
  y: number;
  isSelected: boolean;
  isInner: boolean;
}

export interface HoverPreview {
  value: number; // Snapped value that would be selected on click
  isInner: boolean; // Whether the mouse is in the inner ring (hours 0, 13-23)
  radius: number; // Hand length: OUTER_RADIUS or INNER_RADIUS
  rawAngleDeg: number; // Raw mouse angle (0-360, clockwise from 12h) — used for CSS rotate()
}

@Component({
  selector: 'umss-time-picker-clock',
  standalone: true,
  imports: [],
  templateUrl: './time-picker-clock.component.html',
  styleUrl: './time-picker-clock.component.scss'
})
export class UmssTimePickerClockComponent implements OnChanges {
  @HostBinding('style.display') display = 'block';

  @Input() hours = 0;
  @Input() minutes = 0;
  @Input() view: 'hours' | 'minutes' = 'hours';
  /** Indicates if a real value is selected. When false, does not display the hand or selection. */
  @Input() hasValue = false;
  /** When true, clock renders a single 12-hour ring and shows the AM/PM toggle */
  @Input() use12Hours = false;
  /** Active meridiem period; derived by parent from 24h hours value */
  @Input() amPm: 'AM' | 'PM' = 'AM';

  @Output() hourChange = new EventEmitter<number>();
  @Output() minuteChange = new EventEmitter<number>();
  @Output() viewChange = new EventEmitter<'hours' | 'minutes'>();

  @ViewChild('svgEl') svgEl!: ElementRef<SVGSVGElement>;

  /** Preview value shown while hovering over the dial (null = not hovering) */
  protected hoverPreview: HoverPreview | null = null;

  /**
   * Continuous angle (without normalizing to 0-360) for CSS rotation.
   * Tracked with shortest-path to avoid rotations by the long path.
   */
  protected hoverAngleDeg: number | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    // When switching views (hours ↔ minutes), clears the hover to prevent
    // the accumulated angle from one view interfering with the other.
    if (changes['view'] && !changes['view'].isFirstChange()) {
      this.hoverPreview = null;
      this.hoverAngleDeg = null;
    }
  }

  get clockNumbers(): ClockNumber[] {
    if (this.view === 'hours') {
      if (this.use12Hours) {
        return this.amPm === 'AM'
          ? this.buildHourNumbers12hAM()
          : this.buildHourNumbers12hPM();
      }
      return this.buildHourNumbers();
    }
    return this.buildMinuteNumbers();
  }

  get minuteTicks(): {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    isMajor: boolean;
  }[] {
    return Array.from({ length: 60 }, (_, i) => {
      const angle = clockAngleRad(i, 60);
      const outerR = 110;
      const innerR = i % 5 === 0 ? 100 : 105;
      return {
        x1: SVG_CENTER + outerR * Math.cos(angle),
        y1: SVG_CENTER + outerR * Math.sin(angle),
        x2: SVG_CENTER + innerR * Math.cos(angle),
        y2: SVG_CENTER + innerR * Math.sin(angle),
        isMajor: i % 5 === 0
      };
    });
  }

  get handEndX(): number {
    return SVG_CENTER + this.handRadius * Math.cos(this.selectedAngleRad);
  }

  get handEndY(): number {
    return SVG_CENTER + this.handRadius * Math.sin(this.selectedAngleRad);
  }

  private get selectedAngleRad(): number {
    if (this.view === 'hours') {
      if (this.use12Hours) {
        const h = this.hours;
        let pos: number;
        if (this.amPm === 'AM') {
          // 0 (midnight) sits at 12 o'clock position; 1-11 at their positions
          pos = h === 0 ? 12 : h;
        } else {
          // 12 (noon) at 12 o'clock; 13‣23 at positions 1–11
          pos = h === 12 ? 12 : h - 12;
        }
        return clockAngleRad(pos, 12);
      }
      const pos =
        this.hours === 0 ? 12 : this.hours <= 12 ? this.hours : this.hours - 12;
      return clockAngleRad(pos, 12);
    }
    return clockAngleRad(this.minutes, 60);
  }

  private get handRadius(): number {
    if (this.use12Hours) return OUTER_RADIUS;
    if (this.view === 'hours') {
      return this.hours >= 1 && this.hours <= 12 ? OUTER_RADIUS : INNER_RADIUS;
    }
    return OUTER_RADIUS;
  }

  private buildHourNumbers(): ClockNumber[] {
    const result: ClockNumber[] = [];

    // Outer ring: 1–12
    for (let h = 1; h <= 12; h++) {
      const angle = clockAngleRad(h, 12);
      result.push({
        label: h.toString(),
        value: h,
        x: SVG_CENTER + OUTER_RADIUS * Math.cos(angle),
        y: SVG_CENTER + OUTER_RADIUS * Math.sin(angle),
        isSelected: this.hasValue && this.hours === h,
        isInner: false
      });
    }

    // Inner ring: 13–23
    for (let h = 13; h <= 23; h++) {
      const pos = h - 12;
      const angle = clockAngleRad(pos, 12);
      result.push({
        label: h.toString(),
        value: h,
        x: SVG_CENTER + INNER_RADIUS * Math.cos(angle),
        y: SVG_CENTER + INNER_RADIUS * Math.sin(angle),
        isSelected: this.hasValue && this.hours === h,
        isInner: true
      });
    }

    // 0 at position of 12 (top), inner ring
    const angle0 = clockAngleRad(12, 12);
    result.push({
      label: '00',
      value: 0,
      x: SVG_CENTER + INNER_RADIUS * Math.cos(angle0),
      y: SVG_CENTER + INNER_RADIUS * Math.sin(angle0),
      isSelected: this.hasValue && this.hours === 0,
      isInner: true
    });

    return result;
  }

  /** 12h AM ring: 12(=0), 01, 02, …, 11 */
  private buildHourNumbers12hAM(): ClockNumber[] {
    const result: ClockNumber[] = [];
    // 12 o'clock position = midnight (value 0)
    const angle0 = clockAngleRad(12, 12);
    result.push({
      label: '12',
      value: 0,
      x: SVG_CENTER + OUTER_RADIUS * Math.cos(angle0),
      y: SVG_CENTER + OUTER_RADIUS * Math.sin(angle0),
      isSelected: this.hasValue && this.hours === 0,
      isInner: false
    });
    for (let h = 1; h <= 11; h++) {
      const angle = clockAngleRad(h, 12);
      result.push({
        label: h.toString().padStart(2, '0'),
        value: h,
        x: SVG_CENTER + OUTER_RADIUS * Math.cos(angle),
        y: SVG_CENTER + OUTER_RADIUS * Math.sin(angle),
        isSelected: this.hasValue && this.hours === h,
        isInner: false
      });
    }
    return result;
  }

  /** 12h PM ring: 12(=12), 13, 14, …, 23 */
  private buildHourNumbers12hPM(): ClockNumber[] {
    const result: ClockNumber[] = [];
    // 12 o'clock position = noon (value 12)
    const angle12 = clockAngleRad(12, 12);
    result.push({
      label: '12',
      value: 12,
      x: SVG_CENTER + OUTER_RADIUS * Math.cos(angle12),
      y: SVG_CENTER + OUTER_RADIUS * Math.sin(angle12),
      isSelected: this.hasValue && this.hours === 12,
      isInner: false
    });
    for (let pos = 1; pos <= 11; pos++) {
      const h = pos + 12; // 13–23
      const angle = clockAngleRad(pos, 12);
      result.push({
        label: h.toString(),
        value: h,
        x: SVG_CENTER + OUTER_RADIUS * Math.cos(angle),
        y: SVG_CENTER + OUTER_RADIUS * Math.sin(angle),
        isSelected: this.hasValue && this.hours === h,
        isInner: false
      });
    }
    return result;
  }

  private buildMinuteNumbers(): ClockNumber[] {
    const result: ClockNumber[] = [];
    // Only multiples of 5 are shown and selectable via the dial
    for (let m = 0; m < 60; m += 5) {
      const angle = clockAngleRad(m, 60);
      result.push({
        label: m.toString().padStart(2, '0'),
        value: m,
        x: SVG_CENTER + OUTER_RADIUS * Math.cos(angle),
        y: SVG_CENTER + OUTER_RADIUS * Math.sin(angle),
        isSelected: this.hasValue && this.minutes === m,
        isInner: false
      });
    }
    return result;
  }

  onDialKeyDown(event: KeyboardEvent): void {
    const keys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    if (this.view === 'hours') {
      const delta =
        event.key === 'ArrowUp' || event.key === 'ArrowRight' ? 1 : -1;
      if (this.use12Hours) {
        const min = this.amPm === 'AM' ? 0 : 12;
        const newH = ((this.hours - min + delta + 12) % 12) + min;
        this.hourChange.emit(newH);
      } else {
        this.hourChange.emit((this.hours + delta + 24) % 24);
      }
    } else {
      const delta =
        event.key === 'ArrowUp' || event.key === 'ArrowRight' ? 5 : -5;
      this.minuteChange.emit((this.minutes + delta + 60) % 60);
    }
  }

  onDialMouseMove(event: MouseEvent): void {
    const preview = this.calcPreviewFromEvent(event);
    if (preview) {
      if (this.hoverAngleDeg !== null) {
        // Shortest-path: ensures animation always takes the shortest path
        // (e.g.: 330° → 0° = +30°, not -330°)
        const raw =
          (((preview.rawAngleDeg - (this.hoverAngleDeg % 360)) % 360) + 360) %
          360;
        const diff = raw > 180 ? raw - 360 : raw;
        this.hoverAngleDeg = this.hoverAngleDeg + diff;
      } else {
        // First position: appears directly without animation
        this.hoverAngleDeg = preview.rawAngleDeg;
      }
    } else {
      this.hoverAngleDeg = null;
    }
    this.hoverPreview = preview;
  }

  onDialMouseLeave(): void {
    this.hoverPreview = null;
    this.hoverAngleDeg = null;
  }

  onDialClick(event: MouseEvent): void {
    const preview = this.calcPreviewFromEvent(event);
    if (!preview) return;

    if (this.view === 'hours') {
      this.hourChange.emit(preview.value);
      this.viewChange.emit('minutes');
    } else {
      this.minuteChange.emit(preview.value);
    }
    this.hoverPreview = null;
    this.hoverAngleDeg = null;
  }

  private calcPreviewFromEvent(event: MouseEvent): HoverPreview | null {
    const svg = this.svgEl?.nativeElement;
    if (!svg) return null;

    const rect = svg.getBoundingClientRect();
    const scaleX = 256 / rect.width;
    const scaleY = 256 / rect.height;
    const svgX = (event.clientX - rect.left) * scaleX;
    const svgY = (event.clientY - rect.top) * scaleY;

    const dx = svgX - SVG_CENTER;
    const dy = svgY - SVG_CENTER;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Ignore positions outside the useful clock area
    if (distance < 20 || distance > 118) return null;

    // Raw mouse angle: 0° = 12h, 90° = 3h (clockwise)
    const rawAngleDeg =
      ((((Math.atan2(dy, dx) * 180) / Math.PI + 90) % 360) + 360) % 360;

    // Snapped position to determine which number would be selected
    const snappedStep = Math.round(rawAngleDeg / 30) % 12;

    if (this.view === 'hours') {
      if (this.use12Hours) {
        // Single outer ring; map 12-position step to the correct 24h value
        const value =
          this.amPm === 'AM'
            ? snappedStep === 0
              ? 0 // 12 o'clock = midnight
              : snappedStep
            : snappedStep === 0
              ? 12 // 12 o'clock = noon
              : snappedStep + 12;
        return {
          value,
          isInner: false,
          radius: OUTER_RADIUS,
          rawAngleDeg
        };
      }
      const midRadius = (OUTER_RADIUS + INNER_RADIUS) / 2;
      const isInner = distance < midRadius;
      const value = isInner
        ? snappedStep === 0
          ? 0
          : snappedStep + 12
        : snappedStep === 0
          ? 12
          : snappedStep;
      return {
        value,
        isInner,
        radius: isInner ? INNER_RADIUS : OUTER_RADIUS,
        rawAngleDeg
      };
    } else {
      const value = (snappedStep * 5) % 60;
      return {
        value,
        isInner: false,
        radius: OUTER_RADIUS,
        rawAngleDeg
      };
    }
  }

  /** Moves browser focus to the clock SVG dial. */
  focusDial(): void {
    this.svgEl?.nativeElement.focus();
  }
}
